import { Flex, ImagePlaceholder } from '@solinkcorp/solink-components';
import { cn } from '@solinkcorp/solink-ui';
import { CommonContext } from 'components/AuthenticatedApp/AuthenticatedApp';
import S3Image from 'components/S3Image/S3Image';
import startsWith from 'lodash/startsWith';
import React, { memo, useCallback, useContext, useEffect, useState } from 'react';
import {
  THUMBNAIL_TYPE_CAMERA,
  THUMBNAIL_TYPE_EVENT,
  THUMBNAIL_TYPE_SNAPSHOT,
  type ThumbnailType,
} from 'vd-constants';
import styles from './CameraThumbnail.module.sass';
import { getThumbnailDimensions, matchAspectRatio } from './cameraThumbnailUtils';
export interface CameraThumbnailProps {
  cameraId: string;
  /**
   * Width is the width to display this image.  If undefined, the image will
   * fill the container (width=100%).  If only width or height are specified,
   * then the other will be computed based on the apsect ratio of the image.
   */
  width?: number;
  /** Height to display this image. */
  height?: number;
  /**
   * If true, this will force the image and the container the image is in into
   * a 16x9 aspect ration.  If "containerOnly" or undefined, then the image will
   * be the correct aspect ratio, but will be presented inside a 16x9 container.
   * If false, then the aspect ratio of neither the image nor the container will
   * be constraine.
   */
  forceAspectRatio?: boolean | 'containerOnly';
  loading?: React.ImgHTMLAttributes<HTMLImageElement>['loading'];
  /** When false, the image has no native `title` tooltip. */
  showNativeTitle?: boolean;
  thumbnail?: string;
  thumbnailType?: ThumbnailType;
  spotCheckImage?: boolean;
  className?: string;
}

const CameraThumbnail = (props: CameraThumbnailProps) => {
  const { thumbnailType = THUMBNAIL_TYPE_CAMERA, thumbnail } = props;
  const { camerasDictionary } = useContext(CommonContext);
  const [checkInThumbnailError, setCheckInThumbnailError] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);

  // Reset error state when the thumbnail URL changes so a fresh URL can recover the tile.
  useEffect(() => {
    setHasImageError(false);
  }, [props.thumbnail]);

  const updateImageProcessingState = useCallback((state: boolean) => {
    if (state === false) {
      setCheckInThumbnailError(true);
    }
  }, []);

  const camera = camerasDictionary?.[props.cameraId];

  if (!camera && !thumbnail) {
    return null;
  }

  // TODO: AFAIK isS3Image will always be true.
  const isS3Image = !startsWith(camera?.thumbnail, 'data:image');

  // Work out the width and height of the image, so it will not change size
  // when it loads.
  const imageDimensions = getThumbnailDimensions(camera);

  // Resize the image, keeping the aspect ratio.
  const { width, height } =
    props.width && props.height
      ? props
      : matchAspectRatio(imageDimensions, { width: props.width, height: props.height });

  // By default, use lazy loading for camera thumbnails.
  const loading = props.loading ?? 'lazy';

  if (
    isS3Image &&
    (thumbnailType === THUMBNAIL_TYPE_CAMERA || thumbnailType === THUMBNAIL_TYPE_EVENT)
  ) {
    return (
      <S3Image
        className={cn('camera-thumbnail-wrapper', props.className)}
        id={camera?.id}
        name={props.showNativeTitle === false ? undefined : camera?.name}
        checkInUrl={checkInThumbnailError ? undefined : camera?.thumbnail}
        url={thumbnailType === THUMBNAIL_TYPE_CAMERA ? camera?.thumbnail : props.thumbnail}
        imageWidth={imageDimensions.width}
        imageHeight={imageDimensions.height}
        width={width}
        height={height}
        forceAspectRatio={props.forceAspectRatio}
        loading={loading}
        updateImageProcessingState={updateImageProcessingState}
      />
    );
  }

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      position="static"
      className={props.spotCheckImage ? styles.spotCheckImage : styles.rawCameraThumbnailWrapper}
    >
      {hasImageError ? (
        <div className={styles.placeholderOverlay}>
          <ImagePlaceholder color="inherit" />
        </div>
      ) : (
        <img
          id={thumbnailType === THUMBNAIL_TYPE_SNAPSHOT ? `snapshot-${camera?.id}` : camera?.id}
          alt={camera?.name}
          src={props.thumbnail}
          width={width || '100%'}
          height={height || '100%'}
          loading={loading}
          onError={() => setHasImageError(true)}
        />
      )}
    </Flex>
  );
};

export default memo(CameraThumbnail);
